import React, { useState, useEffect } from 'react';
import {
  Box, Button, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Chip, TextField, InputAdornment, Grid, Card, CardContent,
  CircularProgress, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Switch
} from '@mui/material';
import {
  Search as SearchIcon, Sync as SyncIcon, Business as BusinessIcon,
  CloudSync as CloudSyncIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon,
  FileDownload as FileDownloadIcon, Edit as EditIcon
} from '@mui/icons-material';

import { useAccountService } from '../../services/accountService';
import { useUserAdminService } from '../../services/userAdminService';
import { usePermissions } from '../../hooks/usePermissions';
import { downloadAccountsExcel } from '../../utils/exportAccountsExcel';
import type { User } from '../../types/auth';

interface Account {
  id: number;
  orgId: number;
  orgPath: string;
  countryCode: string;
  empCount?: number;
  sessionCount?: number;
  parentOrgId?: number;
  locationNumber?: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  followUpUser?: {
    id: number;
    username: string;
    fullName: string;
  } | null;
}

interface SyncResult {
  created: number;
  updated: number;
  errors: number;
  total: number;
  message: string;
}

const Accounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [totalAccountsCount, setTotalAccountsCount] = useState<number>(0);
  
  // Filtros y paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [accountStatusFilter, setAccountStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [followUpFilter, setFollowUpFilter] = useState<'all' | 'unassigned' | string>('all');
  const [tableLoading, setTableLoading] = useState(false);
  
  // Exportación
  const [exportLoading, setExportLoading] = useState(false);

  // Sincronización
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  
  // Notificaciones
  const [snackbar, setSnackbar] = useState<any>({ open: false, message: '', severity: 'info' });

  // Asignación de usuario de seguimiento
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [selectedFollowUpUserId, setSelectedFollowUpUserId] = useState<number | null>(null);
  const [followUpUsers, setFollowUpUsers] = useState<User[]>([]);
  const [followUpUsersLoading, setFollowUpUsersLoading] = useState(false);
  const [followUpUsersLoaded, setFollowUpUsersLoaded] = useState(false);
  const [followUpSaving, setFollowUpSaving] = useState(false);

  // Cambio de estatus
  const [statusDialogAccount, setStatusDialogAccount] = useState<Account | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);

  const {
    searchAccounts,
    syncAccounts,
    getAccounts,
    assignFollowUpUser,
    updateAccountStatus
  } = useAccountService();
  const { getUsersByRoleSimple } = useUserAdminService();
  const { hasElevatedAccess } = usePermissions();
  const canManageAccounts = hasElevatedAccess();

  // Cargar cuentas
  const fetchAccounts = async () => {
    try {
      setTableLoading(true);
      const activeFilter = accountStatusFilter === 'all'
        ? undefined
        : accountStatusFilter === 'active';
      const followUpUserId = followUpFilter !== 'all' && followUpFilter !== 'unassigned'
        ? Number(followUpFilter)
        : undefined;
      const unassigned = followUpFilter === 'unassigned';
      const data = await searchAccounts(
        page,
        rowsPerPage,
        searchTerm,
        activeFilter,
        followUpUserId,
        unassigned
      );
      setAccounts(data.content || []);
      setTotalAccountsCount(data.totalElements || 0);
    } catch (err: any) {
      setSnackbar({ open: true, message: 'Error al cargar cuentas', severity: 'error' });
    } finally {
      setTableLoading(false);
    }
  };

  const loadFollowUpUsers = async () => {
    if (followUpUsersLoaded || followUpUsersLoading) return;
    try {
      setFollowUpUsersLoading(true);
      const users = await getUsersByRoleSimple('PHONE_AGENT');
      setFollowUpUsers(users);
      setFollowUpUsersLoaded(true);
    } catch {
      setSnackbar({
        open: true,
        message: 'Error al cargar los usuarios de seguimiento',
        severity: 'error'
      });
    } finally {
      setFollowUpUsersLoading(false);
    }
  };

  // Efectos
  useEffect(() => {
    loadFollowUpUsers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  // Búsqueda con debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchAccounts();
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line
  }, [searchTerm, accountStatusFilter, followUpFilter]);

  // Handlers
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleOpenFollowUpDialog = async (account: Account) => {
    setSelectedAccount(account);
    setSelectedFollowUpUserId(account.followUpUser?.id ?? null);
    setFollowUpDialogOpen(true);
    await loadFollowUpUsers();
  };

  const handleSaveFollowUpUser = async () => {
    if (!selectedAccount || followUpSaving) return;

    try {
      setFollowUpSaving(true);
      const updatedAccount = await assignFollowUpUser(
        selectedAccount.orgId,
        selectedFollowUpUserId
      );

      const remainsInCurrentFilter =
        followUpFilter === 'all' ||
        (followUpFilter === 'unassigned' && !updatedAccount.followUpUser) ||
        (followUpFilter !== 'unassigned' &&
          String(updatedAccount.followUpUser?.id ?? '') === followUpFilter);

      if (remainsInCurrentFilter) {
        setAccounts(currentAccounts =>
          currentAccounts.map(account =>
            account.id === updatedAccount.id ? updatedAccount : account
          )
        );
      } else {
        await fetchAccounts();
      }

      setFollowUpDialogOpen(false);
      setSnackbar({
        open: true,
        message: selectedFollowUpUserId
          ? 'Usuario de seguimiento actualizado'
          : 'Usuario de seguimiento eliminado',
        severity: 'success'
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Error al actualizar el usuario de seguimiento',
        severity: 'error'
      });
    } finally {
      setFollowUpSaving(false);
    }
  };

  const handleChangeAccountStatus = async (accountToUpdate?: Account) => {
    const account = accountToUpdate ?? statusDialogAccount;
    if (!account || statusSaving) return;

    const nextStatus = !account.isActive;
    try {
      setStatusSaving(true);
      const updatedAccount = await updateAccountStatus(account.orgId, nextStatus);
      const remainsInCurrentFilter =
        accountStatusFilter === 'all' ||
        (accountStatusFilter === 'active' && updatedAccount.isActive) ||
        (accountStatusFilter === 'inactive' && !updatedAccount.isActive);

      if (remainsInCurrentFilter) {
        setAccounts(currentAccounts =>
          currentAccounts.map(account =>
            account.id === updatedAccount.id ? updatedAccount : account
          )
        );
      } else {
        await fetchAccounts();
      }

      setStatusDialogAccount(null);
      setSnackbar({
        open: true,
        message: updatedAccount.isActive
          ? 'La cuenta ahora está visible en los formularios'
          : 'La cuenta quedó oculta en los formularios',
        severity: 'success'
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Error al cambiar el estatus de la cuenta',
        severity: 'error'
      });
    } finally {
      setStatusSaving(false);
    }
  };

  const handleStatusToggle = (account: Account) => {
    if (account.isActive) {
      setStatusDialogAccount(account);
      return;
    }
    void handleChangeAccountStatus(account);
  };

  const handleExportExcel = async () => {
    if (exportLoading) return;

    try {
      setExportLoading(true);
      const allAccounts = await getAccounts();

      if (!allAccounts?.length) {
        setSnackbar({
          open: true,
          message: 'No hay cuentas para exportar',
          severity: 'warning',
        });
        return;
      }

      downloadAccountsExcel(allAccounts);
      setSnackbar({
        open: true,
        message: `Se exportaron ${allAccounts.length} cuentas a Excel`,
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Error al exportar el catálogo: ' + error.message,
        severity: 'error',
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleSync = async () => {
    if (syncLoading) return;

    try {
      setSyncLoading(true);
      setSyncDialogOpen(false);
      
      setSnackbar({ 
        open: true, 
        message: 'Iniciando sincronización con ComPsych...', 
        severity: 'info' 
      });

      const result = await syncAccounts();
      setLastSyncResult(result);
      
      // Refrescar la tabla
      await fetchAccounts();
      
      const severity = result.errors > 0 ? 'warning' : 'success';
      setSnackbar({ 
        open: true, 
        message: result.message, 
        severity 
      });

    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: 'Error durante la sincronización: ' + error.message, 
        severity: 'error' 
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Gestión de Cuentas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administrar cuentas organizacionales y sincronizar con ComPsych
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={exportLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExportExcel}
            size="large"
            disabled={exportLoading || syncLoading}
          >
            {exportLoading ? 'Exportando...' : 'Descargar Excel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={syncLoading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            onClick={() => setSyncDialogOpen(true)}
            size="large"
            disabled={syncLoading || exportLoading}
          >
            {syncLoading ? 'Sincronizando...' : 'Sincronizar con ComPsych'}
          </Button>
        </Stack>
      </Box>

      {/* Estadísticas de última sincronización */}
      {lastSyncResult && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {lastSyncResult.created}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Creadas
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <CloudSyncIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {lastSyncResult.updated}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Actualizadas
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <ErrorIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {lastSyncResult.errors}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Errores
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {lastSyncResult.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Procesadas
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SearchIcon color="primary" />
            <Typography variant="h6" color="primary.main">
              Búsqueda de Cuentas
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por nombre de organización, ID o país..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="account-status-filter-label">Visibilidad</InputLabel>
                <Select
                  labelId="account-status-filter-label"
                  value={accountStatusFilter}
                  label="Visibilidad"
                  onChange={event => {
                    setAccountStatusFilter(event.target.value as 'all' | 'active' | 'inactive');
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  <MenuItem value="active">Visibles</MenuItem>
                  <MenuItem value="inactive">Ocultas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="follow-up-filter-label">Usuario de seguimiento</InputLabel>
                <Select
                  labelId="follow-up-filter-label"
                  value={followUpFilter}
                  label="Usuario de seguimiento"
                  onChange={event => {
                    setFollowUpFilter(event.target.value);
                    setPage(0);
                  }}
                  disabled={followUpUsersLoading}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="unassigned">Sin asignar</MenuItem>
                  {followUpUsers.map(user => (
                    <MenuItem key={user.id} value={String(user.id)}>
                      {user.fullName} (@{user.username})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setAccountStatusFilter('all');
                  setFollowUpFilter('all');
                  setPage(0);
                }}
                sx={{ height: '56px' }}
              >
                Limpiar Búsqueda
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {/* Tabla de Cuentas */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
          <Table stickyHeader aria-label="tabla de cuentas">
            <TableHead>
              <TableRow>
                <TableCell>Org ID</TableCell>
                <TableCell>Organización</TableCell>
                <TableCell>País</TableCell>
                <TableCell>Empleados</TableCell>
                <TableCell>Sesiones</TableCell>
                <TableCell>Org Padre</TableCell>
                <TableCell>Usuario de Seguimiento</TableCell>
                <TableCell>Visible en formularios</TableCell>
                <TableCell>Última Actualización</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={24} /> Cargando cuentas...
                  </TableCell>
                </TableRow>
              ) : accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {searchTerm 
                      ? `No se encontraron cuentas que coincidan con "${searchTerm}"`
                      : 'No hay cuentas registradas. Sincroniza con ComPsych para cargar cuentas.'
                    }
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => (
                  <TableRow hover key={account.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                        {account.orgId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {account.orgPath}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={account.countryCode} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {account.empCount ? account.empCount.toLocaleString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {account.sessionCount ? account.sessionCount.toLocaleString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {account.parentOrgId || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {account.followUpUser ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {account.followUpUser.fullName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{account.followUpUser.username}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip label="Sin asignar" size="small" variant="outlined" />
                        )}
                        {canManageAccounts && (
                          <Tooltip title="Cambiar usuario de seguimiento">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenFollowUpDialog(account)}
                              aria-label={`Cambiar usuario de seguimiento de ${account.orgPath}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title={account.isActive ? 'Ocultar de formularios' : 'Mostrar en formularios'}>
                          <span>
                            <Switch
                              checked={account.isActive}
                              onChange={() => handleStatusToggle(account)}
                              disabled={!canManageAccounts || statusSaving}
                              color="success"
                              size="small"
                              inputProps={{
                                'aria-label': `${account.isActive ? 'Ocultar' : 'Mostrar'} ${account.orgPath} en formularios`
                              }}
                            />
                          </span>
                        </Tooltip>
                        <Typography
                          variant="body2"
                          color={account.isActive ? 'success.main' : 'text.secondary'}
                        >
                          {account.isActive ? 'Visible' : 'Oculta'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(account.updatedAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalAccountsCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>

      {/* Confirmación de cambio de estatus */}
      <Dialog
        open={Boolean(statusDialogAccount)}
        onClose={() => !statusSaving && setStatusDialogAccount(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ocultar cuenta de los formularios</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            La cuenta dejará de mostrarse como opción en los formularios de captura.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cuenta: {statusDialogAccount?.orgPath}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStatusDialogAccount(null)}
            color="inherit"
            disabled={statusSaving}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleChangeAccountStatus()}
            variant="contained"
            color="warning"
            disabled={statusSaving}
            startIcon={statusSaving ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {statusSaving ? 'Guardando...' : 'Ocultar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para asignar usuario de seguimiento */}
      <Dialog
        open={followUpDialogOpen}
        onClose={() => !followUpSaving && setFollowUpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cambiar usuario de seguimiento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cuenta: {selectedAccount?.orgPath}
          </Typography>
          <FormControl fullWidth disabled={followUpUsersLoading || followUpSaving}>
            <InputLabel id="follow-up-user-label">Usuario de seguimiento</InputLabel>
            <Select
              labelId="follow-up-user-label"
              value={selectedFollowUpUserId ?? ''}
              label="Usuario de seguimiento"
              onChange={(event) => {
                const value = event.target.value;
                setSelectedFollowUpUserId(value === '' ? null : Number(value));
              }}
            >
              <MenuItem value="">
                <em>Sin asignar</em>
              </MenuItem>
              {selectedAccount?.followUpUser &&
                !followUpUsers.some(user => user.id === selectedAccount.followUpUser?.id) && (
                  <MenuItem value={selectedAccount.followUpUser.id} disabled>
                    {selectedAccount.followUpUser.fullName} (@{selectedAccount.followUpUser.username})
                  </MenuItem>
                )}
              {followUpUsers.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.fullName} (@{user.username})
                </MenuItem>
              ))}
            </Select>
            {followUpUsersLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Cargando usuarios...
                </Typography>
              </Box>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFollowUpDialogOpen(false)}
            color="inherit"
            disabled={followUpSaving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveFollowUpUser}
            variant="contained"
            disabled={followUpUsersLoading || followUpSaving}
            startIcon={followUpSaving ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {followUpSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de sincronización */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon color="primary" />
            Sincronizar con ComPsych
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ¿Estás seguro de que deseas sincronizar las cuentas con el API de ComPsych?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esta operación:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Consultará todas las cuentas disponibles desde ComPsych
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Creará nuevas cuentas que no existan localmente
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Actualizará información de cuentas existentes
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleSync} variant="contained" color="primary">
            Sincronizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Accounts;